import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ediphiPalette } from "../../styles/colors";

interface BannerProps {
  message: string;
  type: "success" | "info" | "warn" | "error";
  onClose: () => void;
  style?: React.CSSProperties;
}

/**
 * One step of each platform severity ramp per role: `-50` fills, `-600` icons, `-800` text (warning
 * reads at `-900`, since its `-800` is too light on its own `-50`). Keep that shape when adding a
 * severity rather than picking a colour by eye.
 */
export const BannerFillColors = {
  success: ediphiPalette.success50,
  info: ediphiPalette.info50,
  warn: ediphiPalette.warning50,
  error: ediphiPalette.error50,
};

export const BannerTextColors = {
  success: ediphiPalette.success800,
  info: ediphiPalette.info800,
  warn: ediphiPalette.warning900,
  error: ediphiPalette.error800,
};

export const BannerIconColors = {
  success: ediphiPalette.success600,
  info: ediphiPalette.info600,
  warn: ediphiPalette.warning600,
  error: ediphiPalette.error600,
};

export const BannerIcons = {
  success: CheckCircleIcon,
  info: InfoIcon,
  warn: WarningIcon,
  error: ErrorIcon,
};

const Banner: React.FC<BannerProps> = ({ message, type, onClose, style }) => {
  const Icon = BannerIcons[type];

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        backgroundColor: BannerFillColors[type],
        color: BannerTextColors[type],
        display: "flex",
        alignItems: "center",
        padding: "8px 16px",
        // Above drawers (1200), because banners are raised from inside the full-screen drawers and at a
        // lower z-index render behind the drawer paper. Deliberately below modals (1300): a banner
        // stacked over a dialog can cover its confirm and cancel actions.
        zIndex: (theme) => theme.zIndex.drawer + 1,
        ...style,
      }}
    >
      <Box sx={{ marginRight: "8px", display: "flex", alignItems: "center" }}>
        <Icon sx={{ fontSize: 24, color: BannerIconColors[type] }} />
      </Box>
      <Typography variant="body2" sx={{ flexGrow: 1, color: BannerTextColors[type] }}>
        {message}
      </Typography>
      <IconButton onClick={onClose} sx={{ color: BannerTextColors[type] }}>
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

export default Banner;
